/**
 * @lines98/client/components/picker — примитив «пикер» (компактный триггер +
 * выпадающий сгруппированный список выбора).
 *
 * ЗАЧЕМ (owner-decision 2026-06-04 + spec extra-modes §5.4). Когда взаимоисключающих
 * опций больше, чем влезает в ряд (>4–5), сегмент-переключатель упирается в ширину и
 * начинает переносить/резать текст. Индустрийная норма: компактный триггер →
 * выпадающий список (Mobbin / Fluent — сегмент рассчитан на 2–5, дальше dropdown).
 * Пикер масштабируется на любое число опций БЕЗ переноса/обрезки (owner-rule
 * «запрещён перенос и …»): список вертикальный, на полную ширину — резать нечего.
 * Поддерживает ГРУППЫ (как семьи режимов в лобби — закон Якоба, консистентность) и
 * disabled-опции с бейджем (locked-режимы «Скоро»).
 *
 * Механика поповера — native `<dialog>` + showModal() (top-layer, ::backdrop,
 * focus-trap, Esc «из коробки»), позиционирование у триггера — общий
 * `lib/popover-anchor` (тот же, что info-popover; без дублирования).
 *
 * CSS — co-located `picker.css`.
 */

import './picker.css';

import { positionNearTrigger } from '../lib/popover-anchor.js';
import { VIEWPORT_APPLIED_EVENT } from '../lib/viewport-sizer.js';

export interface PickerOption {
  /** Стабильный id опции (возвращается в onChange). */
  readonly id: string;
  /** Подпись опции (полная ширина строки списка — не переносится/не режется). */
  readonly label: string;
  /** Недоступна для выбора (locked) — показывается с бейджем, клик игнорируется. */
  readonly disabled?: boolean;
  /** Бейдж справа от подписи (напр. «Скоро» для locked). */
  readonly badge?: string;
}

export interface PickerGroup {
  /** Заголовок группы (напр. семья режимов). Опционален — без него просто список. */
  readonly label?: string;
  /** Опции группы в порядке отображения. */
  readonly options: ReadonlyArray<PickerOption>;
}

export interface PickerOptions {
  /** Группы опций (≥1). */
  readonly groups: ReadonlyArray<PickerGroup>;
  /** Активный id (по умолчанию — первая доступная опция). */
  readonly activeId?: string;
  /** Вызывается при выборе ДРУГОЙ доступной опции. */
  readonly onChange: (id: string) => void;
  /** ARIA-имя пикера. */
  readonly ariaLabel: string;
  /** Необязательный префикс на триггере перед текущей подписью (напр. «Рекорды · »). */
  readonly triggerPrefix?: string;
  /** Доп. класс(ы) экрана-потребителя. */
  readonly className?: string;
}

export interface PickerHandle {
  /** Корневой триггер-кнопка — потребитель вставляет в разметку. */
  readonly element: HTMLButtonElement;
  /** Программно выставить активную опцию (без onChange). */
  setActive(id: string): void;
  /** Закрыть/удалить поповер, снять слушатели. */
  destroy(): void;
}

/** Шеврон «вниз» (SVG — чёткое центрирование, как ICON_* в icon-button). */
const ICON_CHEVRON_DOWN = `<svg class="picker-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6" /></svg>`;
/** Галочка «выбрано» у активной опции. */
const ICON_CHECK = `<svg class="picker-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12l5 5L19 7" /></svg>`;

/** Реестр открытых пикеров — один открыт за раз (авто-закрытие прочих). */
const openRegistry = new Set<() => void>();
function closeAllExcept(except?: () => void): void {
  for (const close of openRegistry) if (close !== except) close();
}

let idCounter = 0;
function nextId(): string {
  idCounter += 1;
  return `picker-${idCounter}`;
}

/** Создать пикер. Возвращает handle с `.element` (триггер). */
export function createPicker(opts: PickerOptions): PickerHandle {
  const id = nextId();
  const allOptions = opts.groups.flatMap((g) => g.options);
  const firstEnabled = allOptions.find((o) => !o.disabled)?.id ?? allOptions[0]?.id ?? '';
  let activeId = opts.activeId ?? firstEnabled;

  const labelOf = (oid: string): string => allOptions.find((o) => o.id === oid)?.label ?? '';

  // ---- Триггер ----
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'picker-trigger';
  if (opts.className) trigger.classList.add(...opts.className.split(/\s+/).filter(Boolean));
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-controls', id);

  const labelEl = document.createElement('span');
  labelEl.className = 'picker-trigger-label';
  const chevronSlot = document.createElement('span');
  chevronSlot.className = 'picker-trigger-chevron';
  chevronSlot.innerHTML = ICON_CHEVRON_DOWN;
  trigger.append(labelEl, chevronSlot);

  const refreshTrigger = (): void => {
    labelEl.textContent = `${opts.triggerPrefix ?? ''}${labelOf(activeId)}`;
    trigger.setAttribute('aria-label', `${opts.ariaLabel}: ${labelOf(activeId)}`);
  };

  // ---- Поповер (native dialog) ----
  const dialog = document.createElement('dialog');
  dialog.className = 'picker-pop';
  dialog.id = id;

  const list = document.createElement('div');
  list.className = 'picker-list';
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', opts.ariaLabel);
  dialog.appendChild(list);
  document.body.appendChild(dialog);

  // Доступные опции в порядке отображения — для клавиш-стрелок.
  const enabledIds = allOptions.filter((o) => !o.disabled).map((o) => o.id);
  const optionEls = new Map<string, HTMLElement>();

  const buildList = (): void => {
    list.innerHTML = '';
    for (const group of opts.groups) {
      if (group.label) {
        const gh = document.createElement('div');
        gh.className = 'picker-group-label';
        gh.setAttribute('role', 'presentation');
        gh.textContent = group.label;
        list.appendChild(gh);
      }
      for (const opt of group.options) {
        const row = document.createElement('div');
        row.className = 'picker-option';
        row.setAttribute('role', 'option');
        row.dataset.id = opt.id;
        if (opt.disabled) {
          row.classList.add('picker-option--disabled');
          row.setAttribute('aria-disabled', 'true');
        } else {
          row.tabIndex = -1;
        }

        const check = document.createElement('span');
        check.className = 'picker-option-check';
        check.innerHTML = ICON_CHECK;

        const text = document.createElement('span');
        text.className = 'picker-option-label';
        text.textContent = opt.label;

        row.append(check, text);

        if (opt.badge) {
          const badge = document.createElement('span');
          badge.className = 'picker-option-badge';
          badge.textContent = opt.badge;
          row.appendChild(badge);
        }

        if (!opt.disabled) {
          row.addEventListener('click', () => choose(opt.id));
        }
        optionEls.set(opt.id, row);
        list.appendChild(row);
      }
    }
    applySelected();
  };

  const applySelected = (): void => {
    for (const [oid, el] of optionEls) {
      el.setAttribute('aria-selected', String(oid === activeId));
      el.classList.toggle('picker-option--selected', oid === activeId);
    }
  };

  // ---- Open / close ----
  let unmounted = false;

  const close = (): void => {
    if (unmounted || !dialog.open) return;
    dialog.close();
    trigger.setAttribute('aria-expanded', 'false');
    openRegistry.delete(close);
  };

  // Перепозиционировать ОТКРЫТЫЙ список у триггера при смене размеров/ориентации
  // (bug-fix 2026-06-25: после поворота телефона список оставался на СТАРОМ месте —
  // positionNearTrigger вызывалась только при open(), а триггер уехал). positionNearTrigger
  // идемпотентна (popover-anchor: «можно дёргать на resize»).
  // РЕСУРС-ЭФФЕКТИВНО (best-practice, owner-rule 2026-06-25): слушаем НЕ сырой `resize`
  // (стреляет десятки раз/сек → layout-трешинг), а ЕДИНЫЙ сигнал viewport-sizer
  // 'lines98:viewport-applied' — он УЖЕ коалесцирован через rAF (раз в кадр) И с
  // dirty-check (только когда геометрия реально изменилась: поворот / адресная строка;
  // на холостые resize молчит). Переиспользуем готовую инфраструктуру вместо своей.
  // Слушатель живёт ТОЛЬКО пока список открыт (вешается в open, снимается в close).
  const onViewportChange = (): void => {
    if (dialog.open) positionNearTrigger(dialog, trigger);
  };

  const open = (): void => {
    if (unmounted) return;
    closeAllExcept(close);
    dialog.showModal();
    positionNearTrigger(dialog, trigger);
    window.addEventListener(VIEWPORT_APPLIED_EVENT, onViewportChange);
    trigger.setAttribute('aria-expanded', 'true');
    openRegistry.add(close);
    // Фокус — на активную опцию (или первую доступную).
    const focusId = enabledIds.includes(activeId) ? activeId : enabledIds[0];
    if (focusId) optionEls.get(focusId)?.focus();
  };

  const choose = (oid: string): void => {
    const changed = oid !== activeId;
    activeId = oid;
    applySelected();
    refreshTrigger();
    close();
    trigger.focus();
    if (changed) opts.onChange(oid);
  };

  // ---- Listeners ----
  const onTriggerClick = (e: Event): void => {
    e.stopPropagation();
    e.preventDefault();
    if (dialog.open) close();
    else open();
  };
  trigger.addEventListener('click', onTriggerClick);

  const onDialogClick = (e: MouseEvent): void => {
    // Клик по подложке (само <dialog>, не по строке) — закрыть.
    if (e.target === dialog) close();
  };
  dialog.addEventListener('click', onDialogClick);

  const onDialogClose = (): void => {
    // Снять reposition-слушатель ВСЕГДА при закрытии (вкл. Esc / unmounted) — до guard'а,
    // чтобы не пережил закрытый список.
    window.removeEventListener(VIEWPORT_APPLIED_EVENT, onViewportChange);
    if (unmounted) return;
    trigger.setAttribute('aria-expanded', 'false');
    openRegistry.delete(close);
  };
  dialog.addEventListener('close', onDialogClose);

  // Клавиши-стрелки по списку (вверх/вниз среди доступных), Enter — выбрать.
  const onListKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const focused = (document.activeElement as HTMLElement)?.dataset?.id;
      const idx = focused ? enabledIds.indexOf(focused) : -1;
      const next =
        e.key === 'ArrowDown'
          ? enabledIds[(idx + 1 + enabledIds.length) % enabledIds.length]
          : enabledIds[(idx - 1 + enabledIds.length) % enabledIds.length];
      if (next) optionEls.get(next)?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      const focused = (document.activeElement as HTMLElement)?.dataset?.id;
      if (focused && enabledIds.includes(focused)) {
        e.preventDefault();
        choose(focused);
      }
    }
  };
  list.addEventListener('keydown', onListKeydown);

  buildList();
  refreshTrigger();

  return {
    element: trigger,
    setActive(oid: string): void {
      if (!optionEls.has(oid)) return;
      activeId = oid;
      applySelected();
      refreshTrigger();
    },
    destroy(): void {
      if (unmounted) return;
      unmounted = true;
      openRegistry.delete(close);
      trigger.removeEventListener('click', onTriggerClick);
      dialog.removeEventListener('click', onDialogClick);
      dialog.removeEventListener('close', onDialogClose);
      list.removeEventListener('keydown', onListKeydown);
      window.removeEventListener(VIEWPORT_APPLIED_EVENT, onViewportChange);
      if (dialog.open) dialog.close();
      if (trigger.parentNode) trigger.parentNode.removeChild(trigger);
      if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
    },
  };
}
